import { useState } from 'react'
import { EyeOff } from 'lucide-react'
import { pollApi } from '../../services/api'
import { useLiveEventStore, type PollRunFull, type QuestionTally } from '../../stores/liveEventStore'

// Full-area, event-app-style poll overlay. Flow: the participant answers ALL
// questions first, THEN lands on a live-updating results page (bar charts for
// CLOSED questions, a tag cloud for OPEN ones). Only the host can close it.
export default function PollOverlay({ channelId, isHost }: { channelId: string; isHost: boolean }) {
  const poll = useLiveEventStore((s) => s.poll)
  const myVotes = useLiveEventStore((s) => s.myVotes)
  const markVoted = useLiveEventStore((s) => s.markVoted)
  const [text, setText] = useState('')
  const [hostSkip, setHostSkip] = useState(false)

  if (!poll || poll.questions.length === 0) return null

  const questions = poll.questions
  const answeredCount = questions.filter((q) => myVotes[q.id]).length
  const allAnswered = answeredCount === questions.length
  const inResults = allAnswered || (isHost && hostSkip)
  const current = questions.find((q) => !myVotes[q.id]) // first unanswered

  const close = () => pollApi.patchRun(channelId, poll.run.id, { state: 'CLOSED' }).catch(() => {})
  const voteClosed = async (q: { id: string }, optionId: string) => {
    markVoted(q.id)
    try { await pollApi.vote(channelId, poll.run.id, { questionId: q.id, optionId }) } catch { /* dedup/closed */ }
  }
  const voteOpen = async (q: { id: string }) => {
    if (!text.trim()) return
    markVoted(q.id)
    try { await pollApi.vote(channelId, poll.run.id, { questionId: q.id, text: text.trim() }) } catch { /* dedup/closed */ }
    setText('')
  }

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-background/95 backdrop-blur-sm">
      {/* Header */}
      <div className="h-14 shrink-0 px-6 flex items-center justify-between border-b border-border">
        <div className="text-sm font-semibold text-foreground">
          Poll{poll.isAnonymous ? ' · anonymous' : ''}
          {!inResults && current && (
            <span className="text-muted-foreground font-normal">
              {' '}· question {answeredCount + 1} of {questions.length}
            </span>
          )}
          {inResults && <span className="text-muted-foreground font-normal"> · results</span>}
        </div>
        {isHost && (
          <button
            onClick={close}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-muted text-foreground hover:bg-muted/80"
          >
            <EyeOff size={16} /> Hide poll
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
        {!inResults && current ? (
          <AnswerView
            question={current}
            text={text}
            setText={setText}
            onVoteClosed={(optionId) => voteClosed(current, optionId)}
            onVoteOpen={() => voteOpen(current)}
          />
        ) : (
          <ResultsView poll={poll} isHost={isHost} />
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 px-6 py-3 border-t border-border flex items-center justify-between">
        {!inResults && isHost && (
          <button onClick={() => setHostSkip(true)} className="text-sm text-primary">
            Show results →
          </button>
        )}
        {inResults && !isHost && (
          <span className="text-sm text-muted-foreground">Waiting for the host to close the poll…</span>
        )}
        <span className="ml-auto text-xs text-muted-foreground">Live · updates in real time</span>
      </div>
    </div>
  )
}

function AnswerView({
  question,
  text,
  setText,
  onVoteClosed,
  onVoteOpen,
}: {
  question: PollRunFull['questions'][number]
  text: string
  setText: (v: string) => void
  onVoteClosed: (optionId: string) => void
  onVoteOpen: () => void
}) {
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-2xl font-semibold text-foreground text-center">{question.text}</div>
      {question.kind === 'CLOSED' ? (
        <div className="space-y-3">
          {question.options.map((o) => (
            <button
              key={o.id}
              onClick={() => onVoteClosed(o.id)}
              className="w-full px-5 py-4 text-left text-lg rounded-xl border border-border bg-card text-foreground hover:border-primary hover:bg-muted transition"
            >
              {o.text}
            </button>
          ))}
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onVoteOpen()}
            placeholder="Type your answer"
            className="flex-1 bg-card text-foreground rounded-xl px-4 py-3 border border-border focus:border-primary outline-none"
          />
          <button onClick={onVoteOpen} className="px-6 rounded-xl bg-primary text-primary-foreground">
            Send
          </button>
        </div>
      )}
    </div>
  )
}

function ResultsView({ poll, isHost }: { poll: PollRunFull; isHost: boolean }) {
  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {poll.questions.map((q, i) => {
        const tally = poll.tallies.find((t) => t.questionId === q.id)
        return (
          <div key={q.id} className="space-y-3">
            <div className="text-lg font-semibold text-foreground">
              <span className="text-muted-foreground">{i + 1}.</span> {q.text}
            </div>
            {q.kind === 'CLOSED' ? (
              <ClosedBars question={q} tally={tally} anonymous={poll.isAnonymous} />
            ) : (
              <OpenTags tally={tally} anonymous={poll.isAnonymous} />
            )}
          </div>
        )
      })}
      {isHost && <div className="text-xs text-muted-foreground">You can keep it open or press “Hide poll”.</div>}
    </div>
  )
}

function ClosedBars({
  question,
  tally,
  anonymous,
}: {
  question: PollRunFull['questions'][number]
  tally?: QuestionTally
  anonymous: boolean
}) {
  const total = tally?.total ?? 0
  return (
    <div className="space-y-2">
      {question.options.map((o) => {
        const entry = tally?.closed?.find((x) => x.optionId === o.id)
        const count = entry?.count ?? 0
        const pct = total ? Math.round((count / total) * 100) : 0
        return (
          <div key={o.id}>
            <div className="flex justify-between text-sm text-foreground mb-1">
              <span>{o.text}</span>
              <span className="text-muted-foreground">{pct}% · {count}</span>
            </div>
            <div className="h-3 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
            {!anonymous && (entry?.voters?.length ?? 0) > 0 && (
              <div className="mt-1 text-[11px] text-muted-foreground truncate">
                {entry!.voters!.map((v) => v.name).join(', ')}
              </div>
            )}
          </div>
        )
      })}
      <div className="text-xs text-muted-foreground pt-1">{total} vote{total === 1 ? '' : 's'}</div>
    </div>
  )
}

function OpenTags({ tally, anonymous }: { tally?: QuestionTally; anonymous: boolean }) {
  const answers = tally?.open ?? []
  if (answers.length === 0) return <div className="text-sm text-muted-foreground">No answers yet.</div>
  return (
    <div className="flex flex-wrap gap-2">
      {answers.map((a) => (
        <span key={a.answerId} className="px-3 py-1.5 rounded-full bg-muted text-foreground text-sm">
          {a.text}
          {!anonymous && a.name && <span className="text-muted-foreground text-xs"> — {a.name}</span>}
        </span>
      ))}
    </div>
  )
}
